import { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet, Link } from "react-router-dom";
import {
  LayoutDashboard, ListChecks, Users, ClipboardCheck, AlertTriangle, PlusCircle,
  Bell, BarChart3, Building2, Network, ScrollText, Settings, LogOut, Search,
  Menu, X, Pill,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { RoleBadge } from "./badges";

const ALL = ["owner", "admin", "manager", "employee"];

const NAV = [
  { section: "Overview" },
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ALL, testid: "sidebar-nav-dashboard" },
  { section: "Tasks" },
  { to: "/tasks", label: "My Tasks", icon: ListChecks, roles: ALL, testid: "sidebar-nav-tasks", exact: true },
  { to: "/tasks?scope=team", label: "Team Tasks", icon: Users, roles: ["owner", "admin", "manager"], testid: "sidebar-nav-team-tasks" },
  { to: "/tasks?status=under_review", label: "Pending Review", icon: ClipboardCheck, roles: ["owner", "admin", "manager"], testid: "sidebar-nav-pending-review" },
  { to: "/tasks?overdue=true", label: "Overdue", icon: AlertTriangle, roles: ALL, testid: "sidebar-nav-overdue" },
  { to: "/tasks/new", label: "Create Task", icon: PlusCircle, roles: ALL, testid: "sidebar-nav-create-task" },
  { section: "Insights" },
  { to: "/notifications", label: "Notifications", icon: Bell, roles: ALL, testid: "sidebar-nav-notifications" },
  { to: "/reports", label: "Task Reports", icon: BarChart3, roles: ALL, testid: "sidebar-nav-reports" },
  { section: "Organization" },
  { to: "/users", label: "Users", icon: Users, roles: ["owner", "admin"], testid: "sidebar-nav-users" },
  { to: "/departments", label: "Departments", icon: Building2, roles: ["owner", "admin"], testid: "sidebar-nav-departments" },
  { to: "/hierarchy", label: "Org Hierarchy", icon: Network, roles: ALL, testid: "sidebar-nav-hierarchy" },
  { to: "/audit", label: "Audit Log", icon: ScrollText, roles: ["owner", "admin"], testid: "sidebar-nav-audit" },
  { to: "/settings", label: "Settings", icon: Settings, roles: ALL, testid: "sidebar-nav-settings" },
];

function NotificationBell() {
  const [count, setCount] = useState(0);
  const [recent, setRecent] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const [c, r] = await Promise.all([
          api.get("/notifications/unread-count"),
          api.get("/notifications?unread_only=true"),
        ]);
        if (mounted) { setCount(c.data.count); setRecent(r.data.slice(0, 5)); }
      } catch (e) { /* ignore */ }
    };
    load();
    const t = setInterval(load, 30000);
    window.addEventListener("notifications-changed", load);
    return () => { mounted = false; clearInterval(t); window.removeEventListener("notifications-changed", load); };
  }, []);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="topbar-notification-bell" className="relative rounded-lg p-2 text-slate-600 transition-colors hover:bg-slate-100 hover:text-primary">
          <Bell className="h-5 w-5" />
          {count > 0 && (
            <span data-testid="topbar-notification-count" className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-bold text-white">
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel>Unread notifications</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {recent.length === 0 && <div className="px-3 py-4 text-sm text-slate-500">You are all caught up.</div>}
        {recent.map((n) => (
          <DropdownMenuItem key={n.id} className="flex cursor-pointer flex-col items-start gap-0.5"
            onClick={() => navigate(n.task_id ? `/tasks/${n.task_id}` : "/notifications")}>
            <span className="text-sm font-semibold text-slate-800">{n.title}</span>
            <span className="line-clamp-2 text-xs text-slate-500">{n.message}</span>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem className="cursor-pointer justify-center text-sm font-semibold text-primary" onClick={() => navigate("/notifications")}>
          View all notifications
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  const doSearch = (e) => {
    e.preventDefault();
    if (search.trim()) navigate(`/tasks?search=${encodeURIComponent(search.trim())}`);
  };

  const items = NAV.filter((i) => i.section || i.roles.includes(user.role));

  const sidebar = (
    <div className="flex h-full flex-col bg-sidebar text-slate-300">
      <div className="flex items-center gap-2.5 border-b border-white/10 px-5 py-5">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary">
          <Pill className="h-5 w-5 text-white" />
        </span>
        <div>
          <div className="font-heading text-base font-bold leading-tight text-white">PharmaTask</div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-teal-400">Enterprise</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {items.map((item, idx) =>
          item.section ? (
            <div key={idx} className="mb-1.5 mt-4 px-3 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 first:mt-0">
              {item.section}
            </div>
          ) : (
            <NavLink
              key={item.to + item.label}
              to={item.to}
              end={item.to === "/"}
              data-testid={item.testid}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `mb-0.5 flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive && !item.to.includes("?")
                    ? "bg-[#1E3A8A] text-white"
                    : "text-slate-300 hover:bg-sidebar-light hover:text-white"
                }`
              }
            >
              <item.icon className="h-4 w-4 shrink-0" />
              {item.label}
            </NavLink>
          )
        )}
      </nav>
      <div className="border-t border-white/10 px-5 py-4">
        <div className="text-sm font-semibold text-white">{user.name}</div>
        <div className="mt-0.5 flex items-center gap-2">
          <RoleBadge role={user.role} />
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <aside className="hidden w-64 shrink-0 lg:block">{sidebar}</aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64">{sidebar}</aside>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center gap-3 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur-md sm:px-6">
          <button className="rounded-lg p-2 hover:bg-slate-100 lg:hidden" onClick={() => setMobileOpen(!mobileOpen)} data-testid="topbar-mobile-menu">
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <form onSubmit={doSearch} className="relative hidden max-w-md flex-1 sm:block">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="topbar-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tasks..."
              className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm outline-none transition-colors focus:border-primary focus:bg-white"
            />
          </form>
          <div className="ml-auto flex items-center gap-2">
            <Link
              to="/tasks/new"
              data-testid="topbar-create-task-button"
              className="hidden items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#1E3A8A] sm:flex"
            >
              <PlusCircle className="h-4 w-4" /> Create Task
            </Link>
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="topbar-user-menu" className="flex items-center gap-2 rounded-lg p-1.5 transition-colors hover:bg-slate-100">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-bold text-white">
                    {user.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-semibold">{user.name}</div>
                  <div className="text-xs font-normal text-slate-500">{user.email}</div>
                  <div className="mt-1"><RoleBadge role={user.role} /></div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/settings")}>
                  <Settings className="mr-2 h-4 w-4" /> Settings
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="topbar-logout-button"
                  className="cursor-pointer text-rose-600"
                  onClick={async () => { await logout(); navigate("/login"); }}
                >
                  <LogOut className="mr-2 h-4 w-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
